import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import SoftKeyboardOverlay from '../components/softKeyboard/SoftKeyboardOverlay';
import { isPublicGuestOrderingRoute } from '../lib/publicGuestRoutes';

type KeyboardType = 'numeric' | 'alphanumeric' | 'alphabetic';

export interface SoftKeyboardRequest {
  id: string;
  type: KeyboardType;
  value: string;
  label?: string;
  maskValue?: boolean;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  /** When true, open the keyboard even if the global soft keyboard toggle is off (e.g. in Register modal) */
  forceOpen?: boolean;
}

interface SoftKeyboardContextValue {
  enabled: boolean;
  toggleEnabled: () => void;
  openKeyboard: (request: SoftKeyboardRequest) => void;
  closeKeyboard: () => void;
  activeRequest: SoftKeyboardRequest | null;
}

const SoftKeyboardContext = createContext<SoftKeyboardContextValue | undefined>(undefined);

const STORAGE_KEY = 'kp_soft_keyboard_enabled';

export const SoftKeyboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return true;
    return stored === 'true';
  });
  const [activeRequest, setActiveRequest] = useState<SoftKeyboardRequest | null>(null);
  const [publicGuestRoute, setPublicGuestRoute] = useState(() => isPublicGuestOrderingRoute());

  useEffect(() => {
    const syncRoute = () => setPublicGuestRoute(isPublicGuestOrderingRoute());
    syncRoute();
    window.addEventListener('popstate', syncRoute);
    window.addEventListener('hashchange', syncRoute);
    return () => {
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('hashchange', syncRoute);
    };
  }, []);

  useEffect(() => {
    if (publicGuestRoute) {
      setActiveRequest(null);
    }
  }, [publicGuestRoute]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(enabled));
    if (!enabled) {
      setActiveRequest(null);
    }
  }, [enabled]);

  const toggleEnabled = () => setEnabled((prev) => !prev);

  const openKeyboard = (request: SoftKeyboardRequest) => {
    // Allow opening when enabled, or when caller requests forceOpen (e.g. Register modal)
    if (!enabled && !request.forceOpen) return;
    const { forceOpen: _, ...rest } = request;
    setActiveRequest({ ...rest });
  };

  const closeKeyboard = useCallback(() => {
    setActiveRequest(null);
    const el = document.activeElement as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      el.removeAttribute('data-soft-keyboard-handled');
      el.blur();
    }
  }, []);

  const value = useMemo(() => ({
    enabled,
    toggleEnabled,
    openKeyboard,
    closeKeyboard,
    activeRequest,
  }), [enabled, activeRequest]);

  return (
    <SoftKeyboardContext.Provider value={value}>
      {children}
      {enabled && !publicGuestRoute && <SoftKeyboardOverlay />}
    </SoftKeyboardContext.Provider>
  );
};

export const useSoftKeyboard = () => {
  const context = useContext(SoftKeyboardContext);
  if (!context) {
    throw new Error('useSoftKeyboard must be used within SoftKeyboardProvider');
  }
  return context;
};
